import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { ResourceType } from "@app/enum/ResourceType";
import { IDriver } from "@app/interfaces/IDriver";
import { IGeofence } from "@app/interfaces/IGeofences";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IAlertRuleGroupMapping } from "@app/main/modules/alerts/_models/alerts.interfaces";
import { AlertsService } from "@app/main/modules/alerts/_services/alerts.service";
import { DriverService } from "@app/services/driver.service";
import { GeofenceService } from "@app/services/geofence.service";
import { GroupService } from "@app/services/groups/group.service";
import { TranslationService } from "@app/services/translation.service";
import { VehicleService } from "@app/services/vehicle.service";
import { UntilDestroy } from "@ngneat/until-destroy";
import { firstValueFrom } from "rxjs";
import { AddGroupRequest } from "../_models/add-group-request";
import { IGroupWithIds } from "../_models/IGroupWithIds";
import { ResourcesAddedRemoved } from "../_models/resources-added-removed";
import { UpdateGroupRequest } from "../_models/update-group-request";
import { GroupsHierarchyHelper } from "../utils/groups-hierarchy-helper";
import { GroupDetailComponent } from "./group-details/group-details.component";
import './group-editor.component.scss';
import { GroupSaveConfirmation, GroupSaveConfirmationComponent } from "./group-save-confirmation/group-save-confirmation.component";

@UntilDestroy()
@Component({
    selector: 'group-editor',
    template: require('./group-editor.component.html')
})
export class GroupEditorComponent implements OnInit, OnChanges {
    @Input() group: IGroupWithIds
    @Input() showTitleAndFooter: boolean;
    @Input() saving: boolean;

    @Output() close = new EventEmitter<void>();
    @Output() newGroup = new EventEmitter<IGroupWithIds>();
    @Output() savingGroup = new EventEmitter<void>();
    @Output() groupSaved = new EventEmitter<IGroupWithIds>();
    @Output() groupDeleted = new EventEmitter<IGroupWithIds>();

    @ViewChild(GroupDetailComponent) groupDetail: GroupDetailComponent;

    ResourceType = ResourceType;

    changedGroup: IGroupWithIds;
    existingDepotVehicleMatchesFound: boolean;
    vehicles: IVehicle[];
    vehicleIds: number[];
    hiddenVehiclesCount = 0;
    drivers: IDriver[];
    driverIds: number[];
    hiddenDriversCount = 0;
    geofences: IGeofence[];
    geofenceIds: number[];
    hiddenGeofencesCount = 0;
    groupAlertMappings: IAlertRuleGroupMapping;
    title: string;

    constructor(
        private readonly driverService: DriverService,
        private readonly geofenceService: GeofenceService,
        private readonly groupService: GroupService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly alertService: AlertsService,
        private readonly detector: ChangeDetectorRef,
        private readonly dialogService: DialogService
    ) { }

    async ngOnInit(): Promise<void> {
        await this.getResources();
        this.checkDepotValidity();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['group']) {
            this.groupChanged();
        }
    }

    get editorHasChanges(): boolean {
        const sameVehicles = this.group.VehicleIds.hasSameValues(this.changedGroup.VehicleIds);
        const sameDrivers = this.group.DriverIds.hasSameValues(this.changedGroup.DriverIds);
        const sameGeofences = this.group.GeofenceIds.hasSameValues(this.changedGroup.GeofenceIds);

        return this.groupDetail.groupHasChanged() || !sameVehicles || !sameDrivers || !sameGeofences;
    }

    closeEditor(): void {
        this.close.emit();
    }

    async saveGroup(): Promise<void> {
        const vehicles = this.buildAddRemoved(this.group.VehicleIds, this.changedGroup.VehicleIds);
        const geofences = this.buildAddRemoved(this.group.GeofenceIds, this.changedGroup.GeofenceIds);
        const drivers = this.buildAddRemoved(this.group.DriverIds, this.changedGroup.DriverIds);

        const areChangesConfirmed = await this.areChangesConfirmedByUser(vehicles);

        if (!areChangesConfirmed) {
            return;
        }

        this.savingGroup.emit();

        const addRequest: AddGroupRequest = {
            name: this.changedGroup.Name,
            isDepot: this.changedGroup.IsDepot,
            parentGroupId: this.changedGroup.ParentId,
            geofenceId: this.changedGroup.GeofenceId,
            vehicles,
            geofences,
            drivers,
        }

        const isAdd = this.group.Id === -1; // Add or Update

        if (isAdd) {
            const id = await this.groupService.addGroup(addRequest);
            this.changedGroup.Id = id;
        } else {
            const updateRequest: UpdateGroupRequest = { ...addRequest, id: this.group.Id };
            await this.groupService.updateGroup(updateRequest);
        }

        this.group = { ...this.changedGroup };
        this.groupSaved.emit(this.group);

        this.groupDetail.updateUnchangedGroup();
    }

    onCreateNewGroup(group: IGroupWithIds): void {
        this.group = group;

        this.groupChanged();
        this.newGroup.emit(group);
    }

    async deleteGroup(confirm: boolean): Promise<void> {
        if (!confirm) {
            return;
        }

        this.savingGroup.emit();
        await this.groupService.deleteGroup(this.group);
        this.groupDeleted.emit(this.group);
    }

    async checkDepotValidity(): Promise<void> {
        this.existingDepotVehicleMatchesFound = false;
        this.detector.detectChanges();

        if (!this.changedGroup.IsDepot ||  !this.changedGroup?.VehicleIds?.length) {
            return;
        }

        const allGroups = await this.groupService.getGroupsWithIds();
        const depots = allGroups.filter(x => x.IsDepot);

        for (let vehicleId of this.changedGroup.VehicleIds) {
            const vehicleInDepot = depots.some(d => d.Id !== this.group.Id && d.VehicleIds.includes(vehicleId));

            if (vehicleInDepot) {
                this.existingDepotVehicleMatchesFound = true;
                break;
            }
        }
    }

    private buildAddRemoved(oldSetOfIds: number[], selectedIds: number[]): ResourcesAddedRemoved {
        const added = selectedIds.filter(x => !oldSetOfIds.includes(x));
        const removed = oldSetOfIds.filter(x => !selectedIds.includes(x));

        return { added, removed };
    }

    private async groupChanged(): Promise<void> {
        const hierarchy = await this.groupService.getGroupsHierarchy();

        this.changedGroup = {
            ...this.group,
            parent: GroupsHierarchyHelper.find(g => g.Id === this.group.ParentId, hierarchy)
        };

        this.title = this.group.Name?.length
            ? this.group.Name
            : this.translationService.t('groups.newGroup');

        this.alertService.getAlertRulesContainsGroupId(this.group.Id).then(response => {
            this.groupAlertMappings = response;
        });
    }

    private async getResources(): Promise<void> {
        const [drivers, geofences, vehicles] = await Promise.all([
            this.driverService.getDrivers() as Promise<IDriver[]>,
            this.geofenceService.getGeofences() as Promise<IGeofence[]>,
            this.vehicleService.getVehiclesBasic() as Promise<IVehicle[]>
        ]);

        this.drivers = drivers;
        this.driverIds = this.drivers.map(d => d.Id);

        this.geofences = geofences;
        this.geofenceIds = this.geofences?.map(g => g.Id);

        this.vehicles = vehicles;
        this.vehicleIds = this.vehicles?.map(v => v.Id);

        this.checkVisibleResources();
    }
    
    private async areChangesConfirmedByUser(vehiclesChanged: ResourcesAddedRemoved): Promise<boolean> {
        if (vehiclesChanged.added.length === 0 && vehiclesChanged.removed.length === 0) {
            return true;
        }

        const ref = this.dialogService.open<boolean, GroupSaveConfirmation>(
            GroupSaveConfirmationComponent, 
            {
                originalGroup: this.group,
                changedGroup: this.changedGroup,
                vehiclesChanged,
                vehicles: this.vehicles
            }
        );

        const dialogResult = await firstValueFrom(ref.afterClosed$);
        return !!dialogResult.data;
    }

    private checkVisibleResources(): void {
        this.hiddenVehiclesCount = this.group.VehicleIds.length - this.vehicleIds.length;
        this.hiddenDriversCount = this.group.DriverIds.length - this.driverIds.length;
        this.hiddenGeofencesCount = this.group.GeofenceIds.length - this.geofenceIds.length;
    }
}
